import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
// Cognitive Flows' own axios instance — same one SubscriptionManagement.jsx
// (billing, also a plain client/src/pages/*.jsx tab) uses for CF routes: no
// CognitiveFlowsProvider needed here, and its x-cf-workspace header default
// ('account') is harmless, since the server treats these three /mcp-server/
// routes as workspace-exempt (they read only the session's email + shop).
import cfApi, { setCognitiveFlowsApiBase } from '../cognitive-flows/utils/api';
import { groupToolsByFamily, sanitizeClientName } from '../cognitive-flows/components/shared/mcpServerTools';

// The user-facing "MCP" section in Account Settings (spec §7.2): the MCP URL,
// copy-paste setup for Claude Code / Claude.ai / ChatGPT, and the signed-in
// user's own connected apps with their per-app tool switches and Disconnect.
//
// Naming note: this is the INBOUND MCP server other apps connect TO. It is
// unrelated to `MCPConnectorsPage` (`client/src/mcp-connectors/`) +
// `services/mcp/`, which is this app acting as an MCP CLIENT to third-party
// servers — deliberately named "McpServerSettings", not anything with
// "Connectors" in it, so the two stay easy to tell apart. It is also
// unrelated to the staff-only "MCP Tools" tab
// (`cognitive-flows/components/dashboard/McpServerToolsManager.jsx`), whose
// family-grouping helper (`shared/mcpServerTools.js#groupToolsByFamily`) is
// reused here rather than re-implemented.
//
// Deliberately plain elements, no react-bootstrap: a bootstrap component
// (e.g. `Container`) calls `useBootstrapPrefix`, a hook reading
// `client/node_modules/react`'s context — a SECOND React copy from this
// component's own `tests/node_modules/react` under Vitest/jsdom, which
// throws "Cannot read properties of null (reading 'useContext')" on render.
// McpServerToolsManager.jsx (task A4's staff tab) sidesteps the same trap the
// same way.
//
// Data comes from `server/services/cognitive-flows/mcpConnectedAppsRoutes.js`:
//   GET    /mcp-server/settings            → { mcpUrl, connectedApps, connectedAppsError }
//   PUT    /mcp-server/apps/:tokenId/tools   { tool, enabled } → { tools }
//          (409 when that tool is no longer available to the app)
//   DELETE /mcp-server/apps/:grantId
//
// Every `tools` entry the server sends is already scope- and staff-filtered
// (spec §5.3) — this component renders exactly what it's given and never
// re-derives which tools an app can see. `tools: null` means the server could
// not load them (the Worker's catalog failed); the app is still listed so it
// can be disconnected. Likewise an app's workspace is shown from the server's
// `workspaceName` / `workspaceDeleted`, and its connection date from
// `createdAt` (an ISO string) — never looked up or converted here.
//
// A switch saves ONE change — `{ tool, enabled }` — and the card's switches
// are then replaced by the `tools` the server answers. The page never sends a
// list of switches: a list it loaded earlier goes stale (staff un-hide a tool,
// another tab saves), and saving a stale list switched the user's own "off"
// back on.
//
// `clientName` is attacker-controlled (OAuth Dynamic Client Registration lets
// an app register under any name). It is read ONLY through `appDisplayName`
// (the consent page's own sanitizer, shared/mcpServerTools.js) and — the part
// that actually protects — it is only ever shown in a place of its own: the
// card's title, and the "App name" field of the Disconnect confirmation. It
// is never put inside a sentence we wrote, so it cannot forge one. The
// confirmation used to be a native `window.confirm`, which takes ONE string:
// a name that closed its own quotation made its injected sentence read as
// ScaleX's own warning, twice, whatever the sanitizer stripped.
//
// Settings.jsx mounts this section the first time its tab is opened (not with
// the Settings page), so the GET below — and, through it, the Worker — only
// runs for a user who looks at MCP.

const s = {
  page: { display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '860px' },
  title: { fontWeight: 800, fontSize: '20px', color: 'var(--text-primary, #101828)', margin: 0 },
  subtitle: { color: 'var(--text-secondary, #667085)', fontSize: '14px', margin: '4px 0 0' },
  card: { background: 'var(--surface,#fff)', border: '1px solid var(--border,#EAECF0)', borderRadius: '14px', overflow: 'hidden' },
  cardHeader: { padding: '14px 20px', borderBottom: '1px solid var(--border,#EAECF0)' },
  cardTitle: { fontWeight: 700, fontSize: '15px', color: 'var(--text-primary, #101828)' },
  cardBody: { padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '14px' },
  hint: { fontSize: '13px', color: 'var(--text-secondary, #475467)', lineHeight: 1.6, margin: 0 },
  list: { fontSize: '13px', color: 'var(--text-secondary, #475467)', lineHeight: 1.8, paddingLeft: '18px', margin: 0 },
  codeRow: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  codeBox: {
    background: '#F9FAFB', border: '1px solid var(--border,#EAECF0)', borderRadius: '8px',
    padding: '8px 12px', fontSize: '13px', wordBreak: 'break-all', flex: '1 1 260px',
  },
  copyBtn: { padding: '7px 14px', borderRadius: '8px', fontWeight: 600, fontSize: '12px', cursor: 'pointer', border: '1px solid var(--border,#D0D5DD)', background: '#fff', color: 'var(--text-primary,#344054)' },
  vendorTabs: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  vendorTabBtn: (active) => ({
    padding: '6px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
    border: `1px solid ${active ? 'var(--cux-ink, #132848)' : 'var(--border,#D0D5DD)'}`,
    background: active ? 'var(--cux-ink, #132848)' : '#fff',
    color: active ? '#fff' : 'var(--text-primary,#344054)',
  }),
  errorBox: {
    fontSize: '13px', color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca',
    borderRadius: '10px', padding: '12px 14px', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap',
  },
  retryBtn: { padding: '6px 14px', borderRadius: '8px', fontWeight: 600, fontSize: '12px', cursor: 'pointer', border: '1px solid #fecaca', background: '#fff', color: '#991b1b' },
  empty: { fontSize: '13px', color: 'var(--text-secondary, #667085)', margin: 0 },
  appCard: { border: '1px solid var(--border,#EAECF0)', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' },
  appHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' },
  appName: { fontWeight: 700, fontSize: '14px', color: 'var(--text-primary, #101828)' },
  appMeta: { fontSize: '12px', color: 'var(--text-secondary, #667085)' },
  disconnectBtn: (disabled) => ({ padding: '6px 14px', borderRadius: '8px', fontWeight: 600, fontSize: '12px', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1, border: '1px solid #fecaca', background: '#fff', color: '#b42318', whiteSpace: 'nowrap' }),
  familyLabel: { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted, #98A2B3)', marginTop: '8px' },
  toolRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #F2F4F7' },
  toolName: { fontSize: '13px', fontWeight: 600, color: 'var(--text-primary, #101828)' },
  toolDesc: { fontSize: '12px', color: 'var(--text-muted, #98A2B3)' },
  toggleBtn: (on, disabled) => ({
    padding: '4px 12px', borderRadius: '8px', fontWeight: 600, fontSize: '12px',
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1, whiteSpace: 'nowrap',
    border: `1px solid ${on ? '#057a55' : 'var(--border,#D0D5DD)'}`,
    background: on ? '#dcfce7' : '#fff',
    color: on ? '#057a55' : 'var(--text-secondary,#475467)',
  }),
  inlineRetryBtn: { marginLeft: '8px', padding: '2px 10px', borderRadius: '8px', fontWeight: 600, fontSize: '12px', cursor: 'pointer', border: '1px solid var(--border,#D0D5DD)', background: '#fff', color: 'var(--text-primary,#344054)' },
  confirmBox: { border: '1px solid #fecaca', background: '#fffbfa', borderRadius: '10px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px' },
  confirmHeading: { fontWeight: 700, fontSize: '14px', color: '#b42318', margin: 0 },
  confirmFields: { display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: '12px', rowGap: '6px', margin: 0, alignItems: 'baseline' },
  confirmLabel: { fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary, #667085)', margin: 0 },
  confirmValue: { fontSize: '13px', color: 'var(--text-primary, #101828)', margin: 0 },
  // The app's self-chosen name: boxed and monospaced, so it reads as a value
  // the app supplied — never as a line of our own text.
  confirmAppName: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: '12px',
    color: 'var(--text-primary, #101828)', background: '#F2F4F7', border: '1px solid var(--border,#D0D5DD)',
    borderRadius: '6px', padding: '3px 8px', margin: 0, wordBreak: 'break-all', justifySelf: 'start',
  },
  confirmActions: { display: 'flex', gap: '8px', justifyContent: 'flex-end' },
  cancelBtn: { padding: '6px 14px', borderRadius: '8px', fontWeight: 600, fontSize: '12px', cursor: 'pointer', border: '1px solid var(--border,#D0D5DD)', background: '#fff', color: 'var(--text-primary,#344054)' },
  confirmDisconnectBtn: (disabled) => ({ padding: '6px 14px', borderRadius: '8px', fontWeight: 600, fontSize: '12px', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1, border: '1px solid #b42318', background: '#b42318', color: '#fff', whiteSpace: 'nowrap' }),
};

const VENDORS = [
  { id: 'claude-code', label: 'Claude Code' },
  { id: 'claude-ai', label: 'Claude.ai' },
  { id: 'chatgpt', label: 'ChatGPT' },
];

/** A URL/command box with its own copy button — the one visual pattern the
 * Connect block and the Claude Code tab share. */
function CopyRow({ text, copyLabel, onCopy }) {
  return (
    <div style={s.codeRow}>
      <code style={s.codeBox}>{text}</code>
      <button type="button" style={s.copyBtn} onClick={() => onCopy(text)}>{copyLabel}</button>
    </div>
  );
}

// Verified against code.claude.com/docs/en/mcp-quickstart (2026-09-21): the
// `claude mcp add --transport http <name> <url>` form, then `/mcp` inside a
// session to authenticate a server that needs sign-in.
function ClaudeCodeSetup({ mcpUrl, onCopy }) {
  const cmd = `claude mcp add --transport http scalex ${mcpUrl}`;
  return (
    <div>
      <p style={s.hint}>In a terminal — not inside a <code>claude</code> session — run:</p>
      <CopyRow text={cmd} copyLabel="Copy command" onCopy={onCopy} />
      <p style={s.hint}>
        Then start Claude Code with <code>claude</code>, run <code>/mcp</code>, select
        &ldquo;scalex&rdquo;, and choose Authenticate to sign in.
      </p>
    </div>
  );
}

// Verified against support.claude.com/en/articles/11175166 (2026-09-21):
// Settings → Connectors → Add custom connector → paste URL → Add → Connect.
function ClaudeAiSetup() {
  return (
    <div>
      <ol style={s.list}>
        <li>In Claude.ai, go to Settings → Connectors (under &ldquo;Customize&rdquo; on some plans).</li>
        <li>Click &ldquo;+&rdquo;, then choose &ldquo;Add custom connector&rdquo;.</li>
        <li>Paste the URL above as the remote MCP server URL, then click &ldquo;Add&rdquo;.</li>
        <li>Click &ldquo;Connect&rdquo; and sign in when prompted.</li>
      </ol>
      <p style={s.hint}>
        Team/Enterprise: an Owner adds the connector once under Organization Settings →
        Connectors before members can connect to it.
      </p>
    </div>
  );
}

// Written from OpenAI's OFFICIAL page, fetched 2026-09-21:
// https://developers.openai.com/api/docs/guides/developer-mode ("How to use").
// Eligibility, the Settings → Security and login switch, and ChatGPT Plugins →
// plus button are its own words; nothing here comes from third-party guides
// (an earlier "Settings → Connectors → Advanced" path did, and was wrong).
// The page lists OAuth among the supported authentication types; that this
// server signs in with OAuth is a fact about THIS server, not a ChatGPT menu.
function ChatGptSetup() {
  return (
    <div>
      <p style={s.hint}>
        Uses ChatGPT developer mode, available to Pro, Plus, Business, Enterprise and
        Education accounts on the web.
      </p>
      <ol style={s.list}>
        <li>In ChatGPT, open Settings → Security and login and turn on Developer mode.</li>
        <li>
          Go to ChatGPT Plugins, select the plus button, and create a developer-mode app for
          this remote MCP server, using the URL above. It signs in with OAuth.
        </li>
        <li>
          In a conversation, choose Developer mode from the composer&apos;s plus menu and pick
          the app. ChatGPT asks you to confirm write actions by default.
        </li>
      </ol>
    </div>
  );
}

/** An app's name, with controls and bidi overrides removed — the only way
 * `clientName` is ever read for display. Shown only in a place of its own
 * (see the file header), never inside a sentence we wrote. */
const appDisplayName = (app) => sanitizeClientName(app?.clientName, 'Unnamed app');

// The Disconnect confirmation's own words. CONSTANT: they never contain the
// app's name (or anything else an app or a user typed), which is shown
// separately in the labelled "App name" field — so no name can add a
// sentence that reads as ours. Access ends "within about a minute", not
// "immediately": a revoked token can keep working that long (KV reads are
// edge-cached ~60 s).
const DISCONNECT_HEADING = 'Disconnect this app?';
const DISCONNECT_EXPLANATION = 'The app below will lose access to your ScaleX data within about a minute. '
  + 'To use it with ScaleX again, you will need to connect it again.';

/** Where the app's grant reaches, from the server's three workspace states. A
 * workspace name is typed by the account's own users, so it is shown through
 * the same sanitizer as an app name, and likewise only in its own field. */
function workspaceLabel(app) {
  if (!app?.workspaceId) return 'Entire account';
  if (app.workspaceDeleted) return 'Deleted workspace';
  return sanitizeClientName(app.workspaceName, 'Unnamed workspace');
}

/** The app's connection date for display, or null. `createdAt` is an ISO
 * 8601 string from the server (or null) — the server already converted the
 * OAuth library's Unix seconds, so it is parsed here as-is. */
function connectedDate(app) {
  const date = app?.createdAt ? new Date(app.createdAt) : null;
  if (!date || Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function connectedLabel(app) {
  const date = connectedDate(app);
  return date ? `Connected ${date}` : 'Connection date unknown';
}

/**
 * The in-page Disconnect confirmation, inside the app's own card. Our heading
 * and explanation are constants; the app's name, workspace and connection
 * date each sit in their own labelled field. Focus lands on Cancel, and
 * Escape cancels.
 */
function DisconnectConfirmation({ app, busy, onCancel, onConfirm }) {
  const cancelRef = useRef(null);
  useEffect(() => { cancelRef.current?.focus(); }, []);
  const headingId = `mcp-disconnect-heading-${app.grantId}`;
  const explanationId = `mcp-disconnect-explanation-${app.grantId}`;

  return (
    <div
      role="alertdialog"
      aria-labelledby={headingId}
      aria-describedby={explanationId}
      style={s.confirmBox}
      onKeyDown={(e) => { if (e.key === 'Escape' && !busy) onCancel(); }}
    >
      <p id={headingId} style={s.confirmHeading}>{DISCONNECT_HEADING}</p>
      <p id={explanationId} style={s.hint}>{DISCONNECT_EXPLANATION}</p>
      <dl style={s.confirmFields}>
        <dt style={s.confirmLabel}>App name</dt>
        <dd style={s.confirmAppName} data-testid="mcp-disconnect-app-name">{appDisplayName(app)}</dd>
        <dt style={s.confirmLabel}>Workspace</dt>
        <dd style={s.confirmValue} data-testid="mcp-disconnect-workspace">{workspaceLabel(app)}</dd>
        <dt style={s.confirmLabel}>Connected</dt>
        <dd style={s.confirmValue} data-testid="mcp-disconnect-connected">{connectedDate(app) || 'Unknown'}</dd>
      </dl>
      <div style={s.confirmActions}>
        <button ref={cancelRef} type="button" style={s.cancelBtn} disabled={busy} onClick={onCancel}>
          Cancel
        </button>
        <button type="button" style={s.confirmDisconnectBtn(busy)} disabled={busy} onClick={onConfirm}>
          {busy ? 'Disconnecting…' : 'Disconnect'}
        </button>
      </div>
    </div>
  );
}

function AppCard({ app, savingTool, disconnecting, onToggleTool, onDisconnect, onRetryTools }) {
  const groups = useMemo(() => groupToolsByFamily(app.tools), [app.tools]);
  const name = appDisplayName(app);
  const busyDisconnect = Boolean(disconnecting[app.grantId]);
  const [confirming, setConfirming] = useState(false);
  // Cancel hands focus back to the Disconnect button that opened the
  // confirmation, rather than dropping it on the page body.
  const disconnectRef = useRef(null);
  const wasConfirming = useRef(false);
  useEffect(() => {
    if (wasConfirming.current && !confirming) disconnectRef.current?.focus();
    wasConfirming.current = confirming;
  }, [confirming]);
  // The tool whose save is in flight for THIS app, if any. Every switch on
  // the card waits for it: each save answers with the app's WHOLE list, so a
  // second save's answer could land first and then be overwritten on screen
  // by the older one.
  const savingToolName = savingTool[app.tokenId];
  const appSaving = savingToolName !== undefined;

  return (
    <div style={s.appCard} data-testid={`mcp-connected-app-${app.grantId}`}>
      <div style={s.appHead}>
        <div>
          <div style={s.appName}>{name}</div>
          <div style={s.appMeta}>Workspace: {workspaceLabel(app)} · {connectedLabel(app)}</div>
          <div style={s.appMeta}>
            Scopes: {Array.isArray(app.scopes) && app.scopes.length ? app.scopes.join(', ') : '—'}
          </div>
        </div>
        {!confirming && (
          <button
            ref={disconnectRef}
            type="button"
            style={s.disconnectBtn(busyDisconnect)}
            disabled={busyDisconnect}
            onClick={() => setConfirming(true)}
          >
            Disconnect
          </button>
        )}
      </div>

      {confirming && (
        <DisconnectConfirmation
          app={app}
          busy={busyDisconnect}
          onCancel={() => setConfirming(false)}
          onConfirm={() => onDisconnect(app)}
        />
      )}

      {!app.manageable ? (
        <p style={s.empty}>Reconnect this app to manage its tools.</p>
      ) : !Array.isArray(app.tools) ? (
        <p style={s.empty}>
          We couldn&apos;t load this app&apos;s tool settings right now. You can still disconnect it.
          <button type="button" style={s.inlineRetryBtn} onClick={onRetryTools}>Try again</button>
        </p>
      ) : groups.length === 0 ? (
        <p style={s.empty}>No tools available for this app.</p>
      ) : (
        groups.map((group) => (
          <div key={group.family}>
            <div style={s.familyLabel}>{group.label}</div>
            {group.tools.map((tool) => (
              <div key={tool.name} style={s.toolRow}>
                <div>
                  <div style={s.toolName}>{tool.name}</div>
                  {tool.description && <div style={s.toolDesc}>{tool.description}</div>}
                </div>
                <button
                  type="button"
                  // The tool's name only. The app is already announced by this
                  // card's region label (aria-labelledby on its heading); folding
                  // the connecting client's self-chosen name into a sentence of
                  // ours would let a screen reader read attacker text as ScaleX's.
                  aria-label={tool.name}
                  disabled={appSaving}
                  style={s.toggleBtn(tool.enabled, appSaving)}
                  onClick={() => onToggleTool(app, tool.name)}
                >
                  {savingToolName === tool.name ? 'Saving…' : tool.enabled ? 'On' : 'Off'}
                </button>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}

export default function McpServerSettings({ app_url } = {}) {
  useEffect(() => {
    if (app_url) setCognitiveFlowsApiBase(app_url);
  }, [app_url]);

  const [settings, setSettings] = useState(null); // { mcpUrl, connectedApps, connectedAppsError }
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [appsReloading, setAppsReloading] = useState(false);
  const [activeVendor, setActiveVendor] = useState('claude-code');
  // tokenId → the tool whose save is in flight for that app (one at a time).
  const [savingTool, setSavingTool] = useState({});
  // Same set, readable synchronously: a second click that lands before React
  // re-renders the disabled switches is still refused.
  const savingAppsRef = useRef(new Set());
  const [disconnecting, setDisconnecting] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const { data } = await cfApi.get('/mcp-server/settings');
      setSettings(data);
    } catch (err) {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // "Try again" on the Connected apps block reloads only that block — the
  // Connect block (URL + setup) stays on screen instead of collapsing into
  // the whole page's loading state.
  const reloadApps = useCallback(async () => {
    setAppsReloading(true);
    try {
      const { data } = await cfApi.get('/mcp-server/settings');
      setSettings((prev) => ({
        ...prev,
        connectedApps: data?.connectedApps ?? null,
        connectedAppsError: data?.connectedAppsError ?? null,
      }));
    } catch (err) {
      setSettings((prev) => ({
        ...prev,
        connectedApps: null,
        connectedAppsError: prev?.connectedAppsError || { code: 'REQUEST_FAILED' },
      }));
    } finally {
      setAppsReloading(false);
    }
  }, []);

  const copy = useCallback(async (text) => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch (err) {
      toast.error('Couldn’t copy — select the text and copy it yourself.');
    }
  }, []);

  /** Replace one app's switches, found by its tokenId. */
  const setAppTools = useCallback((tokenId, tools) => setSettings((prev) => ({
    ...prev,
    connectedApps: Array.isArray(prev?.connectedApps)
      ? prev.connectedApps.map((a) => (a.tokenId === tokenId ? { ...a, tools } : a))
      : prev?.connectedApps ?? null,
  })), []);

  /**
   * Re-read ONE app from the server and put it back on screen as the server
   * has it — or take its card away if it is no longer connected (another tab
   * disconnected it). If the re-read fails, `fallbackTools` (what the card
   * showed before the save) goes back instead.
   */
  const refetchApp = useCallback(async (tokenId, fallbackTools) => {
    try {
      const { data } = await cfApi.get('/mcp-server/settings');
      if (!Array.isArray(data?.connectedApps)) throw new Error('Connected apps unavailable');
      const fresh = data.connectedApps.find((a) => a.tokenId === tokenId);
      setSettings((prev) => ({
        ...prev,
        connectedApps: Array.isArray(prev?.connectedApps)
          ? (fresh
            ? prev.connectedApps.map((a) => (a.tokenId === tokenId ? fresh : a))
            : prev.connectedApps.filter((a) => a.tokenId !== tokenId))
          : prev?.connectedApps ?? null,
      }));
    } catch (err) {
      setAppTools(tokenId, fallbackTools);
    }
  }, [setAppTools]);

  const handleToggleTool = useCallback(async (app, toolName) => {
    if (!app.manageable || !Array.isArray(app.tools)) return;
    const { tokenId } = app;
    const current = app.tools.find((t) => t.name === toolName);
    if (!current || savingAppsRef.current.has(tokenId)) return;
    savingAppsRef.current.add(tokenId);

    // ONE change — this switch, to the opposite of what the card shows — never
    // a list of switches. The card shows it at once; the server's answer then
    // replaces the whole card with what it actually stored.
    const enabled = !current.enabled;
    const prevTools = app.tools;
    setAppTools(tokenId, prevTools.map((t) => (t.name === toolName ? { ...t, enabled } : t)));
    setSavingTool((m) => ({ ...m, [tokenId]: toolName }));

    try {
      const { data } = await cfApi.put(`/mcp-server/apps/${encodeURIComponent(tokenId)}/tools`, { tool: toolName, enabled });
      if (Array.isArray(data?.tools)) setAppTools(tokenId, data.tools);
    } catch (err) {
      const message = err?.response?.data?.error;
      if (err?.response?.status === 409) {
        // The page is out of date: the app can no longer see this tool.
        // Nothing was saved; show the server's sentence and this app's truth.
        toast.error(message || 'That tool is no longer available for this app.');
        await refetchApp(tokenId, prevTools);
      } else {
        setAppTools(tokenId, prevTools);
        toast.error(message || 'Failed to save.');
      }
    } finally {
      savingAppsRef.current.delete(tokenId);
      setSavingTool((m) => {
        const next = { ...m };
        delete next[tokenId];
        return next;
      });
    }
  }, [refetchApp, setAppTools]);

  // Called by the card's own in-page confirmation — see DisconnectConfirmation.
  const handleDisconnect = useCallback(async (app) => {
    setDisconnecting((m) => ({ ...m, [app.grantId]: true }));
    try {
      await cfApi.delete(`/mcp-server/apps/${encodeURIComponent(app.grantId)}`);
      setSettings((prev) => ({
        ...prev,
        connectedApps: Array.isArray(prev?.connectedApps)
          ? prev.connectedApps.filter((a) => a.grantId !== app.grantId)
          : prev?.connectedApps ?? null,
      }));
      // Our words only — the card, and the name with it, has just gone.
      toast.success('App disconnected.');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to disconnect.');
    } finally {
      setDisconnecting((m) => {
        const next = { ...m };
        delete next[app.grantId];
        return next;
      });
    }
  }, []);

  return (
    <div style={s.page}>
      <div>
        <h2 style={s.title}>MCP</h2>
        <p style={s.subtitle}>Connect Claude, ChatGPT and other AI apps to ScaleX.</p>
      </div>

      <div style={s.card}>
        <div style={s.cardHeader}><span style={s.cardTitle}>Connect</span></div>
        <div style={s.cardBody}>
          {loading ? (
            <p style={s.empty}>Loading…</p>
          ) : loadFailed || !settings ? (
            <div style={s.errorBox}>
              <span>We couldn&apos;t load your MCP settings right now.</span>
              <button type="button" style={s.retryBtn} onClick={load}>Try again</button>
            </div>
          ) : !settings.mcpUrl ? (
            <p style={s.hint}>MCP isn&apos;t set up on this environment yet.</p>
          ) : (
            <>
              <CopyRow text={settings.mcpUrl} copyLabel="Copy URL" onCopy={copy} />
              <div style={s.vendorTabs}>
                {VENDORS.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    style={s.vendorTabBtn(activeVendor === v.id)}
                    aria-pressed={activeVendor === v.id}
                    onClick={() => setActiveVendor(v.id)}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
              {activeVendor === 'claude-code' && <ClaudeCodeSetup mcpUrl={settings.mcpUrl} onCopy={copy} />}
              {activeVendor === 'claude-ai' && <ClaudeAiSetup />}
              {activeVendor === 'chatgpt' && <ChatGptSetup />}
            </>
          )}
        </div>
      </div>

      {!loading && settings && settings.mcpUrl && (
        <div style={s.card}>
          <div style={s.cardHeader}><span style={s.cardTitle}>Connected apps</span></div>
          <div style={s.cardBody}>
            {appsReloading ? (
              <p style={s.empty}>Loading…</p>
            ) : settings.connectedAppsError ? (
              <div style={s.errorBox}>
                <span>We couldn&apos;t load your connected apps right now.</span>
                <button type="button" style={s.retryBtn} onClick={reloadApps}>Try again</button>
              </div>
            ) : !Array.isArray(settings.connectedApps) || settings.connectedApps.length === 0 ? (
              <p style={s.empty}>No apps connected yet.</p>
            ) : (
              settings.connectedApps.map((app) => (
                <AppCard
                  key={app.grantId}
                  app={app}
                  savingTool={savingTool}
                  disconnecting={disconnecting}
                  onToggleTool={handleToggleTool}
                  onDisconnect={handleDisconnect}
                  onRetryTools={reloadApps}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
